import axios from 'axios';
import { auth } from './firebase';

// Eine einzige Quelle für die Server-Adresse. Vorher stand in jeder der drei Dateien,
// die den Server rufen, ein eigener Ersatzwert ('/api', 'http://localhost:8000', '') —
// und keiner davon war die echte Produktionsadresse. Beim Veröffentlichen am 10.08.2026
// landete dadurch die lokale Adresse im Live-Build und die Seite fand für jeden Nutzer
// nichts mehr. Der Ersatzwert MUSS deshalb die echte Adresse sein, nicht die lokale.
export const BASE_URL =
  process.env.REACT_APP_API_URL || 'https://kardesmiafix-671442481048.europe-west3.run.app';

const api = axios.create({ baseURL: BASE_URL, timeout: 180000 });

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Profile
export const getProfile = () => api.get('/profile');
export const getAktivesProfil = () => api.get('/profile/aktiv');
export const setAktivesProfil = (dateiname) => api.put('/profile/aktiv', { dateiname });
export const getProfil = (dateiname) => api.get(`/profile/${dateiname}`);
export const createProfil = (daten) => api.post('/profile', daten);
export const updateProfil = (dateiname, daten) => api.put(`/profile/${dateiname}`, daten);
export const deleteProfil = (dateiname) => api.delete(`/profile/${dateiname}`);
export const deletePdf = (dateiname) => api.delete(`/profile/${dateiname}/pdf`);
export const importPdf = (dateiname, datei, signal) => {
  const form = new FormData();
  form.append('datei', datei);
  return api.post(`/profile/${dateiname}/pdf`, form, { signal });
};

export const importPdfFromStorage = (dateiname, storagePath, filename) => {
  const form = new FormData();
  form.append('storage_path', storagePath);
  form.append('filename', filename);
  return api.post(`/profile/${dateiname}/pdf-from-storage`, form);
};

export const extractStellenPdf = (datei) => {
  const form = new FormData();
  form.append('datei', datei);
  return api.post('/stelle/extract-text', form);
};

// Stellenanzeigen
export const getStellen = () => api.get('/stellen');
export const createStelle = (daten) => api.post('/stellen', daten);
export const updateStelle = (id, daten) => api.put(`/stellen/${id}`, daten);
export const deleteStelle = (id) => api.delete(`/stellen/${id}`);
export const jobAbbrechen = (id) => api.post(`/stellen/${id}/job-abbrechen`);
export const fetchStelleText = (id) => api.post(`/stellen/${id}/fetch-text`);
export const getAnalyticsTodos = () => api.get('/analytics/todos');
export const updateAnalyticsTodos = (todos) => api.put('/analytics/todos', { todos });
export const getAnalyticsCalendarEvents = () => api.get('/analytics/calendar-events');
export const updateAnalyticsCalendarEvents = (events) => api.put('/analytics/calendar-events', { events });

// Vorlagen
export const getCVVorlagen = () => api.get('/vorlagen/cv');
export const getAnschreibenVorlagen = () => api.get('/vorlagen/anschreiben');

// Bewerbung
export const starteBewerbung = (daten) => api.post('/bewerbung', daten);
export const starteAnalyse = (daten) => api.post('/bewerbung/analyse', daten);
export const starteGenerierung = (daten) => api.post('/bewerbung/generieren', daten);
export const retryBewerbung = (jobId) => api.post(`/bewerbung/${jobId}/retry`);
export const getBewerbungStatus = (jobId) => api.get(`/bewerbung/${jobId}/status`);
export const rematchKeywords = (stelleId, profilName) =>
  api.post(`/stellen/${stelleId}/rematch`, null, { params: { profil_name: profilName } });
export const ueberarbeiteAbsatz = (daten) => api.post('/anschreiben/absatz-ueberarbeiten', daten);
// Die automatische Kürzung des Anschreibens ist am 2026-08-09 entfernt worden; der
// zugehörige Aufruf ist damit ohne Verwendung. Der Endpunkt bleibt im Backend bestehen,
// die Handy-App des Kollegen ruft ihn weiterhin über ihren eigenen Klienten auf.

// CV-Struktur Einstellungen
export const getCVStruktur = () => api.get('/einstellungen/cv-struktur');
export const saveCVStruktur = (reihenfolge) => api.put('/einstellungen/cv-struktur', { reihenfolge });

// Dokument ohne KI-Call neu bauen (Sektionen umsortieren)
export const rebuildDokument = (stelleId, sektionenReihenfolge) =>
  api.post(`/stellen/${stelleId}/rebuild`, { sektionen_reihenfolge: sektionenReihenfolge });

const _download = async (url, dateiname) => {
  const res = await api.get(url, { responseType: 'blob' });
  const a = document.createElement('a');
  a.href = window.URL.createObjectURL(new Blob([res.data]));
  a.download = dateiname;
  a.click();
  window.URL.revokeObjectURL(a.href);
};

export const downloadCV = (jobId) => _download(`/bewerbung/${jobId}/download/cv`, 'lebenslauf.docx');
export const downloadAnschreiben = (jobId) => _download(`/bewerbung/${jobId}/download/anschreiben`, 'anschreiben.docx');

// Persistente Downloads über stelle_id (funktioniert nach Neustart)
export const downloadStelleCV = (stelleId, dateiname = 'lebenslauf.docx') => _download(`/stellen/${stelleId}/download/cv`, dateiname);
export const downloadStelleAnschreiben = (stelleId, dateiname = 'anschreiben.docx') => _download(`/stellen/${stelleId}/download/anschreiben`, dateiname);

// PDF-Generierung via Playwright (serverseitig)
export const generatePDF = (html, filename) =>
  api.post('/pdf/generate', { html, filename }, { responseType: 'blob' });

// Nutzer-Einstellungen (Spitzname)
export const getNickname = () => api.get('/nutzer/nickname');
export const setNickname = (nickname) => api.put('/nutzer/nickname', { nickname });
export const getNutzerIdentitaet = () => api.get('/nutzer/identitaet');
export const setNutzerIdentitaet = (daten) => api.put('/nutzer/identitaet', daten);

// Auth / Login-Sync
export const loginSync = () => api.post('/auth/login-sync');
export const seiteBesuchenMarkieren = (seite) => api.post('/nutzer/seite-besucht', { seite });
export const tutorialZuruecksetzen = () => api.delete('/nutzer/seite-besucht');
export const sendVerificationEmail = () => api.post('/auth/verification-email');

// Billing & Stripe
export const preiseAbrufen = () => api.get('/checkout/preise');
export const abrechnungAbrufen = () => api.get('/abrechnung');
export const checkoutSessionErstellen = (preisId = null, widerrufsZustimmungTs = null) =>
  api.post('/checkout/session', {
    ...(preisId ? { preis_id: preisId } : {}),
    ...(widerrufsZustimmungTs ? { widerrufs_zustimmung_ts: widerrufsZustimmungTs } : {}),
  });
export const aboKuendigen = () => api.delete('/abrechnung');
export const checkoutVerify = (sessionId) => api.get(`/checkout/verify?session_id=${sessionId}`);

// Admin
export const getAdminNutzer = (limit = 50, offset = 0) =>
  api.get(`/admin/nutzer?limit=${limit}&offset=${offset}`);
export const getAdminNutzerDetail = (uid) => api.get(`/admin/nutzer/${uid}`);
export const setzeRolle = (uid, rolle) =>
  api.post('/admin/nutzer/rolle-setzen', { uid, rolle });
export const getAdminStatistiken = () => api.get('/admin/statistiken');
export const loescheAdminNutzer = (uid) =>
  api.delete(`/admin/nutzer/${uid}`);
export const setzeAdminAbo = (uid, dauerTage = 36500) =>
  api.post(`/admin/nutzer/${uid}/abo`, { dauer_tage: dauerTage });
export const loescheAdminAbo = (uid) =>
  api.delete(`/admin/nutzer/${uid}/abo`);

export const feedbackSenden = (betreff, nachricht, anhaenge = []) =>
  api.post('/feedback', { betreff, nachricht, anhaenge });

// Dev Tools
export const devReset = () => api.delete('/dev/reset');

// Google Calendar
export const gcalAuthUrl       = ()                => api.get('/google-calendar/auth-url');
export const gcalStatus        = ()                => api.get('/google-calendar/status');
export const gcalDisconnect    = ()                => api.delete('/google-calendar/disconnect');
export const gcalEvents        = (von, bis)        => api.get(`/google-calendar/events?von=${von}&bis=${bis}`);
export const gcalEventErstellen = (datum, text, uhrzeit_von = null, uhrzeit_bis = null) =>
  api.post('/google-calendar/events', { datum, text, ...(uhrzeit_von ? { uhrzeit_von, uhrzeit_bis } : {}) });
export const gcalEventLoeschen = (eventId) => api.delete(`/google-calendar/events/${eventId}`);

// Microsoft Calendar (Outlook)
export const msCalAuthUrl        = ()                => api.get('/microsoft-calendar/auth-url');
export const msCalStatus         = ()                => api.get('/microsoft-calendar/status');
export const msCalDisconnect     = ()                => api.delete('/microsoft-calendar/disconnect');
export const msCalEvents         = (von, bis)        => api.get(`/microsoft-calendar/events?von=${von}&bis=${bis}`);
export const msCalEventErstellen = (datum, text, uhrzeit_von = null, uhrzeit_bis = null) =>
  api.post('/microsoft-calendar/events', { datum, text, ...(uhrzeit_von ? { uhrzeit_von, uhrzeit_bis } : {}) });
export const msCalEventLoeschen  = (eventId) => api.delete(`/microsoft-calendar/events/${encodeURIComponent(eventId)}`);

export const speichereOnboardingProfil = (daten) =>
  api.put('/nutzer/onboarding-profil', daten);

export const sendePasswortReset = (email) => api.post('/auth/passwort-reset', { email });

export const deleteAccount = () => api.delete('/nutzer/account');

export const emailAenderungAnfordern = (neue_email) => api.post('/auth/email-aendern', { neue_email });
